import { useState } from "react";
import {
  useClinicVacations,
  useCreateClinicVacation,
  useUpdateClinicVacation,
  useDeleteClinicVacation,
} from "@api/hooks";
import { useAuth } from "@api/useAuth";
import { canManageClinicClosures } from "@utils";
import DateRangeInputs from "./DateRangeInputs";
import { showSuccess, ConfirmModal } from "@common";
import { formatScheduleDate, emptyDateRange, extractApiError } from "./scheduleUtils";

function emptyDraft() {
  return { date_ranges: [emptyDateRange()], reason: "" };
}

export default function ClinicVacationSection({ clinicId }) {
  const { data, isLoading } = useClinicVacations({ clinic: clinicId });
  const vacations = data?.results ?? [];

  // Add/Edit/Delete mirror backend can_manage_clinic_closures (roles 1-4, incl.
  // Practitioner Lead). Roles that can only read (observer/bookkeeper) get the
  // list without write affordances instead of buttons that 403 on submit. The
  // backend still confines roles 3/4 to their own clinics.
  const { user } = useAuth();
  const canEdit = canManageClinicClosures(user);

  const createMut = useCreateClinicVacation();
  const updateMut = useUpdateClinicVacation();
  const deleteMut = useDeleteClinicVacation();

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  function startAdd() {
    setDraft(emptyDraft());
    setEditingId(null);
    setAdding(true);
    setError("");
  }

  function startEdit(vac) {
    setDraft({
      date_ranges:
        vac.date_ranges.length > 0
          ? vac.date_ranges.map((r) => ({ start_date: r.start_date, end_date: r.end_date }))
          : [emptyDateRange()],
      reason: vac.reason ?? "",
    });
    setEditingId(vac.id);
    setAdding(true);
    setError("");
  }

  function cancelEdit() {
    setAdding(false);
    setEditingId(null);
    setDraft(emptyDraft());
    setError("");
  }

  function updateRange(idx, field, value) {
    setDraft((prev) => ({
      ...prev,
      date_ranges: prev.date_ranges.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));
  }

  async function handleSave() {
    setError("");
    const validRanges = draft.date_ranges.filter((r) => r.start_date && r.end_date);
    if (validRanges.length === 0) {
      setError("At least one complete date range is required.");
      return;
    }
    const payload = {
      clinic: clinicId,
      date_ranges: validRanges,
      reason: draft.reason.trim() || null,
    };
    try {
      if (editingId) {
        await updateMut.mutateAsync({ id: editingId, data: payload });
        showSuccess("Closure updated successfully.");
      } else {
        await createMut.mutateAsync(payload);
        showSuccess("Closure saved successfully.");
      }
      cancelEdit();
    } catch (e) {
      setError(extractApiError(e, "Failed to save closure."));
    }
  }

  async function handleDelete() {
    try {
      await deleteMut.mutateAsync(deleteConfirm);
      showSuccess("Closure deleted.");
    } catch {
      // no-op
    } finally {
      setDeleteConfirm(null);
    }
  }

  const isSaving = createMut.isPending || updateMut.isPending;

  return (
    <div className="mt-2">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-palette-slate-heading">Closure Periods</h3>
        {canEdit && !adding && (
          <button
            type="button"
            onClick={startAdd}
            className="text-xs font-medium text-palette-blue-badge hover:text-palette-blue-text"
          >
            + Add
          </button>
        )}
      </div>

      {isLoading && <p className="text-xs text-palette-gray-placeholder">Loading...</p>}

      {!isLoading && vacations.length === 0 && !adding && (
        <p className="text-xs text-palette-gray-placeholder">No closures recorded.</p>
      )}

      <div className="space-y-2">
        {vacations.map((vac) => (
          <div
            key={vac.id}
            className="overflow-hidden rounded-xl border border-palette-gray-divider bg-white"
          >
            <div className="flex items-start justify-between px-4 py-3">
              <div className="flex-1">
                <div className="space-y-1">
                  {vac.date_ranges.map((r, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-palette-blue-indicator shrink-0" />
                      <span className="text-sm text-palette-slate-heading">
                        {formatScheduleDate(r.start_date)} – {formatScheduleDate(r.end_date)}
                      </span>
                    </div>
                  ))}
                </div>
                {vac.reason && (
                  <p className="mt-1.5 text-xs text-palette-gray-secondary italic">{vac.reason}</p>
                )}
              </div>
              {canEdit && (
                <div className="ml-3 flex shrink-0 items-center gap-3">
                  <button
                    type="button"
                    onClick={() => startEdit(vac)}
                    className="text-xs text-palette-blue-label hover:text-palette-blue-heading"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(vac.id)}
                    disabled={deleteMut.isPending}
                    className="text-xs text-palette-red-indicator hover:text-palette-red-badge disabled:opacity-40"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {canEdit && adding && (
        <div className="mt-3 rounded-xl border border-palette-blue-border bg-palette-blue-soft/40 p-4">
          <div className="mb-3 text-xs font-semibold text-palette-blue-heading">
            {editingId ? "Edit closure" : "New closure"}
          </div>

          {error && <p className="mb-2 text-xs text-palette-red-label">{error}</p>}

          <DateRangeInputs
            ranges={draft.date_ranges}
            onChange={updateRange}
            onAdd={() =>
              setDraft((prev) => ({
                ...prev,
                date_ranges: [...prev.date_ranges, emptyDateRange()],
              }))
            }
            onRemove={(idx) =>
              setDraft((prev) => ({
                ...prev,
                date_ranges: prev.date_ranges.filter((_, i) => i !== idx),
              }))
            }
          />

          <textarea
            value={draft.reason}
            onChange={(e) => setDraft((prev) => ({ ...prev, reason: e.target.value }))}
            placeholder="Reason (optional, e.g. Holiday, Staff Training)"
            rows={2}
            className="mt-3 w-full resize-none rounded-md border border-palette-gray-divider bg-white px-3 py-2 text-xs text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-indicator"
          />

          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={cancelEdit}
              className="text-xs font-medium text-palette-gray-secondary hover:text-palette-gray-heading"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="rounded-lg bg-palette-blue-badge px-3 py-1.5 text-xs font-medium text-white hover:bg-palette-blue-heading disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete closure?"
        message="This closure will be permanently removed and cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleteMut.isPending}
      />
    </div>
  );
}
